import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import '../pages/word-view-page.js'
import '../pages/about-page.js'
import '../pages/login-page.js'
import '../pages/user-management-page.js'
import '../pages/create-entry-page.js'
import '../pages/edit-entry-page.js'
import '../pages/meaning-etymologies-page.js'
import '../pages/edit-language-page.js'
import styles from './app-router.css?inline'

/**
 * Simple router component for SPA navigation
 */
@customElement('app-router')
export class AppRouter extends LitElement {
  static styles = css`${unsafeCSS(styles)}`

  @state()
  private currentRoute: string = window.location.pathname || '/'

  connectedCallback() {
    super.connectedCallback()
    // React to auth changes from other tabs (localStorage)
    window.addEventListener('storage', () => {
      if ((this.currentRoute === '/create' || this.currentRoute === '/edit' || this.currentRoute === '/languages' || this.currentRoute === '/users') && !this.isLoggedIn()) {
        this.redirectToDictionary()
      }
      this.requestUpdate()
    })
    window.addEventListener('popstate', () => {
      let path = window.location.pathname
      if ((path === '/create' || path === '/edit' || path === '/languages' || path === '/users') && !this.isLoggedIn()) {
        path = '/'
        window.history.replaceState({}, '', path)
      }
      if (path === '/login' && this.isLoggedIn()) {
        path = '/'
        window.history.replaceState({}, '', path)
      }
      this.currentRoute = path
      this.handleRouteChange()
      this.requestUpdate()
    })

    window.addEventListener('auth-changed', () => {
      if ((this.currentRoute === '/create' || this.currentRoute === '/edit' || this.currentRoute === '/languages' || this.currentRoute === '/users') && !this.isLoggedIn()) {
        this.redirectToDictionary()
      }
    })

    // Handle link clicks
    document.addEventListener('click', (e) => {
      const link = (e.target as HTMLElement).closest('a[data-route]')
      if (link) {
        e.preventDefault()
        const route = link.getAttribute('data-route')
        if (route) {
          this.navigate(route)
        }
      }
    })

    this.handleRouteChange()
  }

  private isLoggedIn(): boolean {
    return !!localStorage.getItem('auth-token')
  }

  private redirectToDictionary() {
    window.history.replaceState({}, '', '/')
    this.currentRoute = '/'
    this.handleRouteChange()
    this.requestUpdate()
  }

  private navigate(route: string) {
    if ((route === '/create' || route === '/edit' || route === '/languages' || route === '/users') && !this.isLoggedIn()) {
      this.redirectToDictionary()
      return
    }
    if (route === '/login' && this.isLoggedIn()) {
      this.redirectToDictionary()
      return
    }
    window.history.pushState({}, '', route)
    this.currentRoute = route
    this.handleRouteChange()
    this.requestUpdate()
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated?.(changedProperties)
    if ((this.currentRoute === '/create' || this.currentRoute === '/edit' || this.currentRoute === '/languages' || this.currentRoute === '/users') && !this.isLoggedIn()) {
      this.redirectToDictionary()
    }
  }

  private handleRouteChange() {
    // Update active nav links
    document.querySelectorAll('nav a[data-route]').forEach(link => {
      const route = link.getAttribute('data-route')
      if (route === this.currentRoute) {
        link.classList.add('active')
      } else {
        link.classList.remove('active')
      }
    })
  }

  render() {
    if (this.currentRoute.startsWith('/meaning-etymologies/')) {
      const meaningId = this.currentRoute.replace('/meaning-etymologies/', '')
      return html`<meaning-etymologies-page meaningId=${meaningId}></meaning-etymologies-page>`
    }
    switch (this.currentRoute) {
      case '/':
        return html`<word-view-page></word-view-page>`
      case '/about':
        return html`<about-page></about-page>`
      case '/login':
        if (this.isLoggedIn()) {
          this.redirectToDictionary()
          return html`<word-view-page></word-view-page>`
        }
        return html`<login-page></login-page>`
      case '/users':
        return html`<user-management-page></user-management-page>`
      case '/create':
        return html`<create-entry-page></create-entry-page>`
      case '/edit': {
        const params = new URLSearchParams(window.location.search)
        const meaningId = params.get('meaningId') ?? ''
        return html`<edit-entry-page .initialMeaningId=${meaningId}></edit-entry-page>`
      }
      case '/languages': {
        const params = new URLSearchParams(window.location.search)
        const languageId = params.get('languageId') ?? ''
        return html`<edit-language-page .initialLanguageId=${languageId}></edit-language-page>`
      }
      default:
        return html`<div class="not-found">Page not found</div>`
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-router': AppRouter
  }
}
